/**
 * Delegação de atribuições (backend: api/routes/delegacoes.py — docs/PLANO_DELEGACAO.md).
 *
 * O titular delega a um membro do escritório (ou convida por e-mail), em Leitura ou
 * Colaboração, com validade e Chat de IA opcionais. O delegado vê as recebidas e
 * escolhe atuar numa delas — o contexto em si vive em `lib/delegacao/atuacao`.
 *
 * Todas estas rotas são da PESSOA (nunca levam `X-Atuando-Por`).
 */
import type { NivelDelegacao } from '@/lib/delegacao/atuacao';

import { api } from './client';

/** Calculada no backend a cada leitura (sem side effect). */
export type SituacaoDelegacao =
  | 'ATIVA'
  | 'PENDENTE'
  | 'CONVITE_EXPIRADO'
  | 'VENCIDA'
  | 'FORA_DO_ESCRITORIO'
  | 'TITULAR_SEM_OAB'
  | 'TITULAR_EQUIPE_INTERNA'
  | 'DELEGADO_EQUIPE_INTERNA'
  | 'ENCERRADA';

export interface PessoaResumo {
  id: string;
  nome: string;
  email: string;
}

export interface Delegacao {
  id: string;
  titular: PessoaResumo;
  delegado: PessoaResumo | null;
  email_convidado: string | null;
  escritorio: { id: string; nome: string };
  nivel: NivelDelegacao;
  permite_ia: boolean;
  situacao: SituacaoDelegacao;
  valida_ate: string | null;
  notificacoes: boolean;
  created_at: string;
  ativada_em: string | null;
}

export interface MembroElegivel {
  usuario_id: string;
  nome: string;
  email: string;
  escritorio_id: string;
  escritorio_nome: string;
  /** Delegação vigente já dada a este membro, se houver. */
  delegacao_id: string | null;
}

export interface ResumoRecebida {
  delegacao_id: string;
  titular_id: string;
  titular_nome: string;
  nivel: NivelDelegacao;
  intimacoes_nao_lidas: number;
  eventos_proximos_7_dias: number;
}

export interface HistoricoDelegacao {
  acao: string;
  alvo: string;
  resource_id: string | null;
  created_at: string;
  /** Sobre o quê: nome do cliente, título e data do evento, nome do documento… */
  detalhe: string | null;
  /** Número CNJ do processo envolvido, quando a ação é sobre um. */
  processo: string | null;
}

export interface DelegacaoCreate {
  escritorio_id: string;
  delegado_id?: string;
  email?: string;
  nivel: NivelDelegacao;
  valida_ate?: string | null;
  permite_ia: boolean;
  ciencia_sigilo: boolean;
  ciencia_ia: boolean;
}

export interface DelegacaoUpdate {
  nivel?: NivelDelegacao;
  permite_ia?: boolean;
  ciencia_ia?: boolean;
  /** `null` explícito remove a validade. */
  valida_ate?: string | null;
}

export async function fetchDelegacoesDadas(): Promise<Delegacao[]> {
  const { data } = await api.get<Delegacao[]>('/delegacoes/dadas');
  return data;
}

export async function fetchDelegacoesRecebidas(): Promise<Delegacao[]> {
  const { data } = await api.get<Delegacao[]>('/delegacoes/recebidas');
  return data;
}

export async function fetchResumoRecebidas(): Promise<ResumoRecebida[]> {
  const { data } = await api.get<ResumoRecebida[]>('/delegacoes/recebidas/resumo');
  return data;
}

export async function fetchMembrosElegiveis(): Promise<MembroElegivel[]> {
  const { data } = await api.get<MembroElegivel[]>('/delegacoes/elegiveis');
  return data;
}

export async function fetchHistoricoDelegacao(id: string): Promise<HistoricoDelegacao[]> {
  const { data } = await api.get<HistoricoDelegacao[]>(`/delegacoes/${id}/historico`);
  return data;
}

export async function criarDelegacao(body: DelegacaoCreate): Promise<Delegacao> {
  const { data } = await api.post<Delegacao>('/delegacoes', body);
  return data;
}

export async function alterarDelegacao(id: string, body: DelegacaoUpdate): Promise<Delegacao> {
  const { data } = await api.patch<Delegacao>(`/delegacoes/${id}`, body);
  return data;
}

export async function revogarDelegacao(id: string): Promise<void> {
  await api.delete(`/delegacoes/${id}`);
}

export async function renunciarDelegacao(id: string): Promise<void> {
  await api.post(`/delegacoes/${id}/renunciar`);
}

export async function definirNotificacoesDelegacao(id: string, ativo: boolean): Promise<void> {
  await api.put(`/delegacoes/${id}/notificacoes`, { ativo });
}

/**
 * Validade escolhida num `<input type="date">` → fim daquele dia em Brasília.
 * "Válida até 30/09" tem de valer o dia 30 inteiro, não parar à meia-noite UTC.
 */
export function fimDoDiaBrasilia(dataIso: string): string {
  return `${dataIso}T23:59:59-03:00`;
}
